/**
 * The financial-account subtypes Mantle currently supports across the product —
 * the one source of truth shared by the consumer web display and the backend
 * assistant agent surface.
 *
 * Named by enum **key** (e.g. `CHECKING`), not by stored value, because the
 * layers hold different string *values* for the same subtype: the GraphQL
 * surface exposes the enum key names over the wire (type-graphql's
 * `registerEnumType(FinancialAccountSubtype)` — so web's codegen'd enum has
 * `Checking = 'CHECKING'`), while the DB stores a lowercase variant
 * (`CHECKING = 'checking'`). Each consumer resolves these keys through its own
 * `FinancialAccountSubtype` enum.
 *
 * To support a new subtype (e.g. money-market) under an already-supported
 * primary type, add its key here — both surfaces widen off this one list.
 */

/** Depository subtypes Mantle displays/answers on (checking + savings). */
export const DEPOSITORY_SUBTYPE_KEYS = ['CHECKING', 'SAVINGS'] as const;

/** Credit subtypes Mantle displays/answers on (credit card only). */
export const CREDIT_CARD_SUBTYPE_KEYS = ['CREDIT_CARD'] as const;

/**
 * Investment subtypes Mantle answers on once the household's investment
 * accounts are enabled. Kept out of the supported union: the union drives the
 * web's account sections, which do not display these yet. Plaid has no
 * rollover subtype — a rollover arrives as `IRA`.
 */
export const INVESTMENT_SUBTYPE_KEYS = [
  'BROKERAGE',
  'IRA',
  'ROTH',
  'SEP_IRA',
  'SIMPLE_IRA',
  'PLAN_401K',
  'PLAN_401A',
  'PLAN_403B',
  'PLAN_457B',
  'ROTH_401K',
  'PENSION',
  'HSA',
  'PLAN_529',
] as const;

/** Every supported subtype key, ordered for display (drives web section order). */
export const SUPPORTED_FINANCIAL_ACCOUNT_SUBTYPE_KEYS = [
  ...DEPOSITORY_SUBTYPE_KEYS,
  ...CREDIT_CARD_SUBTYPE_KEYS,
] as const;

export type SupportedFinancialAccountSubtypeKey = (typeof SUPPORTED_FINANCIAL_ACCOUNT_SUBTYPE_KEYS)[number];
